import { Box, CircularProgress, Typography } from "@mui/material";
import { Add } from "iconsax-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { FloatingTextField, PageHeader, SefanoButton } from "@/components/Molecules";
import { PageTemplate } from "@/components/Templates/Page";
import type { ProfileTemplateProps } from "./Profile.template.types";
import * as styles from "./Profile.template.styles";

export type { ProfileTemplateProps } from "./Profile.template.types";

export function ProfileTemplate({
  isLoading,
  isUpdating,
  isChangingPassword,
  passwordSuccess,
  stores,
  accountForm,
  passwordForm,
  onAccountSubmit,
  onPasswordSubmit,
}: ProfileTemplateProps) {
  const { t } = useTranslation("profile");

  if (isLoading) {
    return (
      <PageTemplate>
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <PageHeader title={t("title")} subtitle={t("subtitle")} />

      <Box sx={styles.card}>
        <Typography sx={styles.sectionTitle}>{t("account.title")}</Typography>
        <Box component="form" onSubmit={onAccountSubmit} sx={styles.formStack}>
          <FloatingTextField
            label={t("account.fullName")}
            error={Boolean(accountForm.formState.errors.full_name)}
            helperText={accountForm.formState.errors.full_name?.message}
            {...accountForm.register("full_name")}
          />
          <FloatingTextField
            label={t("account.username")}
            error={Boolean(accountForm.formState.errors.username)}
            helperText={accountForm.formState.errors.username?.message}
            {...accountForm.register("username")}
          />
          <FloatingTextField
            label={t("account.phone")}
            error={Boolean(accountForm.formState.errors.phone)}
            helperText={accountForm.formState.errors.phone?.message}
            {...accountForm.register("phone")}
          />
          <SefanoButton type="submit" disabled={isUpdating}>
            ذخیره
          </SefanoButton>
        </Box>
      </Box>

      <Box sx={styles.card}>
        <Typography sx={styles.sectionTitle}>{t("password.title")}</Typography>
        <Box component="form" onSubmit={onPasswordSubmit} sx={styles.formStack}>
          <FloatingTextField
            label={t("password.current")}
            type="password"
            error={Boolean(passwordForm.formState.errors.current_password)}
            helperText={passwordForm.formState.errors.current_password?.message}
            {...passwordForm.register("current_password")}
          />
          <FloatingTextField
            label={t("password.new")}
            type="password"
            error={Boolean(passwordForm.formState.errors.new_password)}
            helperText={passwordForm.formState.errors.new_password?.message}
            {...passwordForm.register("new_password")}
          />
          <FloatingTextField
            label={t("password.confirm")}
            type="password"
            error={Boolean(passwordForm.formState.errors.confirm_password)}
            helperText={passwordForm.formState.errors.confirm_password?.message}
            {...passwordForm.register("confirm_password")}
          />
          {passwordSuccess ? (
            <Typography sx={styles.successText}>{t("password.success")}</Typography>
          ) : null}
          <SefanoButton type="submit" disabled={isChangingPassword}>
            {t("password.submit")}
          </SefanoButton>
        </Box>
      </Box>

      <Box sx={styles.card}>
        <Typography sx={styles.sectionTitle}>{t("shops.title")}</Typography>
        {stores.length === 0 ? (
          <Typography color="text.secondary">{t("shops.empty")}</Typography>
        ) : (
          stores.map((shop) => (
            <Box key={shop.id} sx={styles.shopRow}>
              <Typography variant="body2" fontWeight={600}>
                {shop.name}
              </Typography>
            </Box>
          ))
        )}
        <Link to="/shops/create" style={{ textDecoration: "none" }}>
          <SefanoButton intent="add" startIcon={<Add size={18} variant="Linear" />}>
            {t("shops.create")}
          </SefanoButton>
        </Link>
      </Box>
    </PageTemplate>
  );
}
